import {
  Button,
  Col,
  Container,
  OverlayTrigger,
  Row,
  Spinner,
  Table,
  Tooltip,
} from "react-bootstrap";
import { Recording } from "./interfaces/Recording";
import { useAppSelector } from "../../hooks";
import ExportFormatDropdown from "./ExportFormatDropdown";
import { useMemo, useState, useCallback, memo } from "react";
import { buildAcceptString } from "./lib/exportHelper";
import {
  requestWithAuthorization,
  requestWithCookie,
} from "../../network/apiRequests";
import CometSpinner from "../CometSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faFilePdf,
  faGears,
  faPerson,
  faShapes,
} from "@fortawesome/free-solid-svg-icons";
import { MdContentCopy } from "react-icons/md";

interface RecordingTableProps {
  userId: string | undefined;
  recordings: Recording[];
  onBack: () => void;
  isAdmin: boolean;
}

const RecordingTable = memo(function RecordingTable({
  userId,
  recordings,
  onBack,
  isAdmin,
}: RecordingTableProps) {
  const [exportFormat, setExportFormat] = useState<string>("csv");
  const [isLoading, setIsLoading] = useState<number[]>([]);

  const linkBuilder = useCallback((recordingId: string | null) => {
    if (recordingId === null) {
      return "#";
    }
    return `${process.env.REACT_APP_API_HOSTNAME}/download/${recordingId}?fileType=${exportFormat}`;
  }, [exportFormat]);

  const handleReportDownloadClick = useCallback(async (
    reportId: string | null,
    index: number,
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    if (reportId === null) {
      return;
    }

    setIsLoading((prevLoading) => [...prevLoading, index]);

    try {
      const url = `${
        process.env.REACT_APP_API_HOSTNAME
      }/report/download?reportId=${encodeURIComponent(reportId)}`;
      const response = await requestWithAuthorization(url, { responseType: "blob" });
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      var filename = "report.pdf";
      if (contentDisposition) {
        // Try different patterns for filename extraction
        let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
        if (!filenameMatch) {
          filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
        }
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, '');
        }
      }

      // Use the content-type from the response if available
      const contentType = response.headers['content-type'] || "application/pdf";
      
      const blob = new Blob([response.data], { type: contentType });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();

      // Clean up
      if (link.parentNode != null) {
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      console.error("Error fetching report: ", error);
    } finally {
      setIsLoading((prevLoading) => prevLoading.filter((i) => i !== index));
    }
  }, []);

  const downloadRecording = useCallback(async (
    userId: string | undefined,
    recordingId: string | null,
    index: number,
    downloadType: 'raw' | 'combined' | 'processed' = 'raw'
  ) => {
    if (userId === undefined || recordingId === null) {
      return "#";
    }

    setIsLoading((prevLoading) => [...prevLoading, index]);

    let url = linkBuilder(recordingId);
    if (downloadType === 'combined') {
      url += '&combined=true';
    } else if (downloadType === 'processed') {
      url += '&processed=true';
    }

    try {
      const response = await requestWithAuthorization(url, { responseType: "blob" });
      if (response.status === 200) {
        // Extract filename from Content-Disposition header if available
        const contentDisposition = response.headers['content-disposition'];
        var filename = `recording_${downloadType}.${exportFormat}`;
        if (contentDisposition) {
          // Try different patterns for filename extraction
          let filenameMatch = contentDisposition.match(/filename\*?=['"]?([^'";]+)['"]?/i);
          if (!filenameMatch) {
            filenameMatch = contentDisposition.match(/filename=([^;]+)/i);
          }
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].trim().replace(/^['"]|['"]$/g, '');
          }
        }

        // Use the content-type from the response if available
        const contentType = response.headers['content-type'] || buildAcceptString(exportFormat);
        
        const blob = new Blob([response.data], {
          type: contentType,
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        // Clean up
        if (link.parentNode != null) {
          link.parentNode.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      }
      // Approach to start download in Browser (shows a blank page for a few seconds)
      // window.open(url, "_blank");
    } catch (error) {
      console.error("Error fetching recording: ", error);
    } finally {
      setIsLoading((prevLoading) => prevLoading.filter((i) => i !== index)); // remove the index from the array of loading indices
    }
  }, [linkBuilder, exportFormat]);

  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "descending",
  });

  const sortedRecordings = useMemo(() => {
    let sortableRecordings = [...recordings];
    sortableRecordings.sort((a: Recording, b: Recording) => {
      if (a.timestamp < b.timestamp) {
        return sortConfig.direction === "ascending" ? -1 : 1;
      }
      if (a.timestamp > b.timestamp) {
        return sortConfig.direction === "ascending" ? 1 : -1;
      }
      return 0;
    });
    return sortableRecordings;
  }, [recordings, sortConfig]);

  const handleSort = useCallback(() => {
    setSortConfig(prev => ({
      key: "timestamp",
      direction: prev.direction === "ascending" ? "descending" : "ascending",
    }));
  }, []);

  const copyRecordingId = useCallback((recordingId: string | null) => {
    if (recordingId === null) {
      return;
    }
    navigator.clipboard.writeText(recordingId);
  }, []);

  return (
    <>
      <Container fluid className="pb-3">
        {isAdmin && (
          <Row>
            <Col>
              <Button variant="outline-light" onClick={onBack}>
                Back
              </Button>
            </Col>
            <Col className="justify-content-end text-end">
              <ExportFormatDropdown
                exportFormat={exportFormat}
                setExportFormat={setExportFormat}
              />
            </Col>
          </Row>
        )}
      </Container>
      <Container fluid>
        <Table striped bordered hover variant="dark">
          <thead>
            <tr>
              <th>#</th>
              <th>Recording Name</th>
              <th onClick={handleSort} className="clickable-header">
                Start Timestamp
                {sortConfig.direction === "ascending" ? " ↓" : " ↑"}
              </th>
              <th>Duration</th>
              <th>Download</th>
            </tr>
          </thead>
          <tbody>
            {sortedRecordings.map((recording, index) => {
              const formated_timestamp = new Date(
                recording.timestamp
              ).toLocaleString("de-DE", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
              });
              return (
                <tr key={recording.recordingId}>
                  <td>{index + 1}</td>
                  <td>
                    <div className="d-flex justify-content-between">
                      <span>{recording.name}</span>
                      <OverlayTrigger
                        overlay={
                          <Tooltip id={recording.recordingId}>
                            Copy Recording ID
                          </Tooltip>
                        }
                      >
                        <span
                          className="text-end justify-content-end"
                          onClick={() => copyRecordingId(recording.recordingId)}
                          style={{ cursor: "pointer" }}
                          title="Copy Recording ID"
                        >
                          <MdContentCopy />
                        </span>
                      </OverlayTrigger>
                    </div>
                  </td>
                  <td>{formated_timestamp}</td>
                  <td>{recording.duration}</td>
                  <td className="text-center">
                    {isLoading.includes(index) ? (
                      <Spinner animation="border" variant="light" />
                    ) : (
                      isAdmin && (
                        <>
                          <FontAwesomeIcon
                            icon={faDownload}
                            color="white"
                            size="lg"
                            className="me-3"
                            onClick={() =>
                              downloadRecording(
                                userId,
                                recording.recordingId,
                                index,
                                'raw'
                              )
                            }
                            cursor={"pointer"}
                            title="Download Raw Data"
                          />
                          <FontAwesomeIcon
                            icon={faShapes}
                            color="white"
                            size="lg"
                            className="me-3"
                            onClick={() =>
                              downloadRecording(
                                userId,
                                recording.recordingId,
                                index,
                                'combined'
                              )
                            }
                            cursor={"pointer"}
                            title="Download Combined Data"
                          />
                          <FontAwesomeIcon
                            icon={faGears}
                            color="white"
                            size="lg"
                            className="me-3"
                            onClick={() =>
                              downloadRecording(
                                userId,
                                recording.recordingId,
                                index,
                                'processed'
                              )
                            }
                            cursor={"pointer"}
                            title="Download Processed Data"
                          />
                        </>
                      )
                    )}
                    {recording.reportExisting && (
                      isLoading.includes(index + 1000) ? (
                        <Spinner animation="border" variant="light" size="sm" />
                      ) : (
                        <a
                          href={"#"}
                          onClick={(e) =>
                            handleReportDownloadClick(recording.recordingId, index + 1000, e)
                          }
                          target="_blank"
                        >
                          <FontAwesomeIcon
                            icon={faFilePdf}
                            color="white"
                            size="lg"
                            title="Download Report"
                          />
                        </a>
                      )
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Container>
    </>
  );
});

export default RecordingTable;
